import { Link, useParams, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowRight, CheckCircle2, Trophy, ExternalLink, Play, Facebook } from "lucide-react";
import { Seo } from "../components/Seo";
import { Reveal } from "../components/Reveal";
import { Marquee } from "../components/Marquee";
import { TEAMS, LINKS, IMAGES } from "../data/club";

export default function TeamDetail() {
  const { slug } = useParams();
  const team = TEAMS.find((t) => t.slug === slug);
  if (!team) return <Navigate to="/teams" replace />;
  const others = TEAMS.filter((t) => t.slug !== slug).slice(0, 3);
  const sideImage = team.sideImage || "/assets/pitch-ready.webp";

  return (
    <main data-testid={`team-page-${team.slug}`}>
      <Seo title={`${team.name} — Whitley Bay Cricket Club`} description={`${team.name} at Whitley Bay Cricket Club. ${team.league}. ${team.tagline}`} />

      <section className="relative pt-32 sm:pt-40 pb-16 overflow-hidden" data-testid="team-header">
        <div className="absolute inset-0 bg-cover bg-center opacity-25" style={{ backgroundImage: `url(${team.image})` }} aria-hidden="true" />
        <div className="absolute inset-0 bg-gradient-to-b from-[#0B1520]/70 via-[#0B1520]/85 to-[#0B1520]" aria-hidden="true" />
        <div className="relative max-w-7xl mx-auto px-5 sm:px-8">
          <Link to="/teams" className="inline-flex items-center gap-2 text-sm text-[#9BB0C1] hover:text-[#E5AC3C] transition-colors mb-8" data-testid="team-back-link">
            <ArrowLeft size={15} /> All teams
          </Link>
          <p className="text-xs font-mono-club uppercase tracking-[0.25em] text-[#E5AC3C] mb-4">{team.group} · {team.league}</p>
          <h1 className="font-head text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight leading-[1.05]">
            <span className="block overflow-hidden">
              <motion.span className="block" initial={{ y: "110%" }} animate={{ y: 0 }} transition={{ duration: 0.8, delay: 0.1, ease: [0.22, 1, 0.36, 1] }}>
                {team.name}
              </motion.span>
            </span>
            <span className="block overflow-hidden">
              <motion.span className="block font-serif-accent font-medium text-[#E5AC3C] text-2xl sm:text-3xl lg:text-4xl mt-2" initial={{ y: "110%" }} animate={{ y: 0 }} transition={{ duration: 0.8, delay: 0.22, ease: [0.22, 1, 0.36, 1] }}>
                {team.tagline}
              </motion.span>
            </span>
          </h1>
          {team.badge && (
            <motion.div
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
              className="mt-6 inline-flex items-center gap-2 rounded-full border border-[#E5AC3C]/40 bg-[#0B1520]/60 px-4 py-2"
              data-testid="team-badge"
            >
              <Trophy size={14} className="text-[#E5AC3C]" />
              <span className="text-xs font-bold tracking-wide">{team.badge}</span>
            </motion.div>
          )}
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-5 sm:px-8 pb-16 grid gap-12 lg:grid-cols-[1.4fr_1fr]">
        <Reveal>
          <p className="text-base sm:text-lg text-[#9BB0C1] leading-relaxed" data-testid="team-description">{team.description}</p>
          {team.fundingNote && (
            <div className="mt-6 rounded-2xl border border-[#E5AC3C]/30 bg-[#1B4D3E]/20 p-5" data-testid="team-funding-note">
              <p className="text-sm text-[#F7F5F0] leading-relaxed">
                <span className="font-bold text-[#E5AC3C]">Free indoor sessions: </span>
                we have secured funding from Active North Tyneside for indoor sessions — free to attend, and all equipment provided.
              </p>
            </div>
          )}
          <ul className="mt-8 space-y-3">
            {team.highlights.map((h) => (
              <li key={h} className="flex items-start gap-3 text-sm sm:text-base text-[#F7F5F0]" data-testid={`team-highlight-${h.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                <CheckCircle2 size={18} className="text-[#2A8C68] mt-0.5 shrink-0" /> {h}
              </li>
            ))}
          </ul>
          {team.accreditation && (
            <div className="mt-8 inline-flex items-center gap-4 rounded-2xl border border-white/10 bg-white px-5 py-4" data-testid="team-accreditation-badge">
              <img src={IMAGES.clubmark} alt="ECB Clubmark accreditation logo" className="h-11 w-auto" loading="lazy" />
              <span className="text-[11px] font-bold text-[#0B1520] uppercase tracking-[0.12em] leading-tight">
                ECB Clubmark<br />Accredited Club
              </span>
            </div>
          )}
          {team.pathway && (
            <figure className="mt-8 rounded-2xl border border-white/10 bg-white p-4 max-w-xl" data-testid="team-pathway-figure">
              <img src={IMAGES.pathway} alt="Whitley Bay CC player pathway from All Stars through the junior age groups to the 1st XI" className="w-full h-auto rounded-lg" loading="lazy" />
              <figcaption className="mt-3 text-[11px] font-bold text-[#0B1520] uppercase tracking-[0.12em]">
                The WBCC player pathway — All Stars to 1st XI
              </figcaption>
            </figure>
          )}
          <div className="mt-10 flex flex-wrap gap-4">
            <Link to="/contact" data-testid="team-join-cta" className="inline-flex items-center gap-2 bg-[#E5AC3C] hover:bg-[#F3C35B] text-[#0B1520] font-bold px-6 py-3 rounded-full text-sm transition-colors">
              Get involved <ArrowRight size={15} />
            </Link>
            <a href={LINKS.playCricket} target="_blank" rel="noopener noreferrer" data-testid="team-playcricket-cta" className="inline-flex items-center gap-2 border border-white/20 hover:border-[#E5AC3C] hover:text-[#E5AC3C] font-semibold px-6 py-3 rounded-full text-sm transition-colors">
              Fixtures & results <ExternalLink size={14} />
            </a>
          </div>
        </Reveal>
        <Reveal delay={0.15}>
          <div className={`rounded-3xl overflow-hidden border border-white/10 ${team.poster ? "bg-white" : ""}`}>
            <img
              src={sideImage}
              alt={team.poster ? `${team.name} poster — Whitley Bay Cricket Club` : "The square at West Park, ready for play"}
              className={`w-full h-[380px] ${team.poster ? "object-contain p-2" : "object-cover"}`}
              loading="lazy"
              data-testid="team-side-image"
            />
          </div>
        </Reveal>
      </section>

      {team.reel && (
        <section className="max-w-7xl mx-auto px-5 sm:px-8 pb-16" data-testid="team-reel-section">
          <Reveal>
            <div className="rounded-3xl border border-white/10 bg-[#122030] p-8 sm:p-10 grid gap-8 md:grid-cols-2 items-center">
              <div>
                <p className="text-xs font-mono-club uppercase tracking-[0.25em] text-[#E5AC3C] mb-3">Watch</p>
                <h2 className="font-head text-2xl sm:text-3xl font-extrabold tracking-tight">
                  Our girls' festival, <span className="font-serif-accent font-medium text-[#E5AC3C]">in action</span>
                </h2>
                <p className="mt-3 text-[#9BB0C1] text-sm sm:text-base leading-relaxed">
                  Catch the atmosphere from our girls' cricket festival — video from the club's Facebook page.
                </p>
                <a href={team.reel} target="_blank" rel="noopener noreferrer" data-testid="team-reel-link" className="mt-5 inline-flex items-center gap-2 text-sm font-bold text-[#E5AC3C] hover:text-[#F3C35B] transition-colors">
                  Watch on Facebook <ExternalLink size={14} />
                </a>
              </div>
              <div className="flex flex-col gap-4">
                <a
                  href={team.reel}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group relative block rounded-2xl overflow-hidden border border-white/10"
                  data-testid="team-reel-link-card"
                >
                  <img src={IMAGES.girls} alt="Whitley Bay CC girls' squad at their first festival" className="w-full h-[280px] object-cover group-hover:scale-105 transition-transform duration-700" loading="lazy" />
                  <div className="absolute inset-0 bg-[#0B1520]/45 flex items-center justify-center">
                    <span className="w-16 h-16 rounded-full bg-[#E5AC3C] text-[#0B1520] flex items-center justify-center shadow-[0_0_30px_rgba(229,172,60,0.4)] group-hover:scale-110 transition-transform">
                      <Play size={26} fill="currentColor" />
                    </span>
                  </div>
                  <div className="absolute bottom-0 inset-x-0 bg-gradient-to-t from-[#0B1520]/90 to-transparent p-4 pt-10">
                    <span className="text-sm font-bold text-[#F7F5F0]">Watch the festival reel on Facebook</span>
                  </div>
                </a>
                <a
                  href={LINKS.facebook}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group flex items-center gap-4 rounded-2xl border border-white/10 bg-[#0B1520] p-5 hover:border-[#E5AC3C]/40 transition-colors"
                  data-testid="team-facebook-card"
                >
                  <span className="w-11 h-11 rounded-full bg-[#1877F2] flex items-center justify-center shrink-0">
                    <Facebook size={20} className="text-white" />
                  </span>
                  <div>
                    <span className="font-head font-bold text-sm block">More girls' cricket on Facebook</span>
                    <span className="text-xs text-[#9BB0C1]">Photos, videos and festival news from the club page</span>
                  </div>
                  <ExternalLink size={15} className="ml-auto text-[#5C7285] group-hover:text-[#E5AC3C] transition-colors shrink-0" />
                </a>
              </div>
            </div>
          </Reveal>
        </section>
      )}

      <Marquee items={["Whitley Bay Cricket Club", team.name, team.league, "West Park", "Est. 1926"]} testId="team-marquee" />

      <section className="max-w-7xl mx-auto px-5 sm:px-8 py-20">
        <Reveal>
          <h2 className="font-head text-2xl sm:text-3xl font-extrabold tracking-tight mb-8">More from the club</h2>
        </Reveal>
        <div className="grid gap-5 sm:grid-cols-3">
          {others.map((t, i) => (
            <Reveal key={t.slug} delay={i * 0.08}>
              <Link to={`/teams/${t.slug}`} data-testid={`team-related-${t.slug}`}>
                <div className="group h-full rounded-2xl border border-white/10 bg-[#122030] p-6 hover:border-[#E5AC3C]/40 transition-colors">
                  <h3 className="font-head text-lg font-bold group-hover:text-[#E5AC3C] transition-colors">{t.name}</h3>
                  <p className="mt-1 text-xs font-mono-club uppercase tracking-[0.15em] text-[#5C7285]">{t.league}</p>
                  <p className="mt-3 text-sm text-[#9BB0C1]">{t.tagline}</p>
                </div>
              </Link>
            </Reveal>
          ))}
        </div>
      </section>
    </main>
  );
}
